<template>
  <div
    :class="[
      'UIInputCheckbox',
      {
        'UIInputCheckbox--no-margin': noMargin,
        'UIInputCheckbox--has-errors': hasErrors,
        'UIInputCheckbox--is-disabled': isDisabled,
      },
      $attrs.class,
    ]"
  >
    <label
      class="UIInputCheckbox__label"
      :for="inputId"
      :data-cy="`input[${name}]`"
      :data-testid="`input[${name}]`"
    >
      <slot name="label">
        {{ label }}
      </slot>

      <input
        :id="inputId"
        class="UIInputCheckbox__input"
        type="checkbox"
        v-bind="$attrs"
        :checked="value"
        @input="emitValue"
        @keydown.enter.prevent=""
        :disabled="isDisabled"
      />

      <span class="UIInputCheckbox__checkmark" />
    </label>

    <template v-for="error in errors" :key="error">
      <div class="UIInputCheckbox__error">
        {{ error }}
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useField } from 'vee-validate'
import { toRef, computed, watch, getCurrentInstance } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    label?: string
    name: string
    modelValue?: boolean
    isDisabled?: boolean
    noMargin?: boolean
    id?: string
  }>(),
  {
    modelValue: undefined,
  },
)
const emit = defineEmits(['change', 'input', 'update:modelValue'])

const emitValue = (event: any) => {
  value.value = event.target.checked
  emit('input', event.target.checked) // Emit checkbox state
  // emit('update:modelValue', event.target.checked) // Emit checkbox state
}

const hasErrors = computed(() => errors.value && errors.value.length)
const { value, errors } = useField(
  toRef(props, 'name'),
  {},
  { initialValue: props.modelValue, syncVModel: true },
)

// Ensure DOM id uniqueness across multiple forms/components using the same field name
const instance = getCurrentInstance()
const uid = instance ? instance.uid : Math.random().toString(36).slice(2)
const inputId = computed(() => props.id ?? `${props.name}-${uid}`)
watch(value, (val) => {
  emit('change', val)
})
</script>

<style scoped lang="scss">
.UIInputCheckbox {
  position: relative;
  display: flex;
  height: 1.5rem;
  align-items: center;
  position: relative;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  --active-color: #{$newcolor-grey-300};
  --label-color: #{$newcolor-secondary-main};

  &.UIInputCheckbox--no-margin {
    margin-bottom: 0;
  }

  &.UIInputCheckbox--has-errors {
    --active-color: #{$newcolor-danger-main};
    --label-color: #{$newcolor-danger-main};
  }

  &.UIInputCheckbox--is-disabled {
    pointer-events: none;

    .UIInputCheckbox__input {
      .UIInputCheckbox__checkmark {
        background-color: $newcolor-grey-400;
      }

      &:checked ~ .UIInputCheckbox__checkmark {
        background-color: $newcolor-grey-400;
        border-color: $newcolor-grey-400;
      }
    }
  }

  &:hover {
    .UIInputCheckbox__input ~ .UIInputCheckbox__checkmark {
      background-color: $newcolor-grey-100;
    }
  }

  .UIInputCheckbox__label {
    font-size: 0.875rem;
    color: var(--label-color);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-direction: row-reverse;
  }

  .UIInputCheckbox__input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .UIInputCheckbox__checkmark {
      background-color: $color-ui-accent-main;
      border-color: $color-ui-accent-main;

      &::after {
        display: block;
      }
    }
  }

  .UIInputCheckbox__checkmark {
    position: relative;
    display: flex;
    top: 0;
    left: 0;
    height: 1.5rem;
    width: 1.5rem;
    background-color: $color-white;
    cursor: pointer;
    border-radius: 8px;
    border: 2px solid var(--active-color);

    &::after {
      content: '';
      position: absolute;
      display: none;
      left: 0.375rem;
      top: 0.125rem;
      width: 0.5rem;
      height: 0.75rem;
      border: solid $color-white;
      border-width: 0 3px 3px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
  .UIInputCheckbox__error {
    font-size: 0.75rem;
    line-height: 1.25rem;
    color: $newcolor-danger-main;
    position: absolute;
    bottom: -1.25rem;
  }
}
</style>
